import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Table from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Checkbox from '@cloudscape-design/components/checkbox';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { getLeadTimeService } from '../services';
import type { LeadTimeItem, LeadTimeHistoryEntry } from '../services/types';

// Supplier Gateway · Lead Time Update.
// Editable: lead time, end of sale, end of life, hazard class, DG goods.
// Read-only: country code. Edits are keyed by item id so they persist across
// pagination; changes are reviewed before saving. Per-item audit history opens
// in a modal. Item data comes from the lead-time service (empty until wired).

type Edit = {
    leadTime?: number | null;
    endOfSale?: string;
    endOfLife?: string;
    hazardClass?: string;
    dgGoods?: boolean;
};

const HAZARD_CLASSES = [
    'None',
    'Class 1',
    'Class 2',
    'Class 3',
    'Class 4',
    'Class 5',
    'Class 6',
    'Class 7',
    'Class 8',
    'Class 9',
];
const HAZARD_OPTIONS = HAZARD_CLASSES.map((h) => ({ value: h, label: h }));

const PAGE_SIZE = 25;

const CSV_HEADERS = [
    'Item',
    'Description',
    'LeadTimeDays',
    'EndOfSale',
    'EndOfLife',
    'HazardClass',
    'DGGoods',
    'Country',
];

const csvEscape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

function normalizeDate(v: string): string {
    const s = v.trim();
    if (s === '') return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
        const [, m, d, y] = mdy;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return s;
}

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// Minimal CSV parser (handles quoted fields and commas within quotes).
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1;
                } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === ',') {
            row.push(field);
            field = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n') i += 1;
            row.push(field);
            field = '';
            if (row.some((c) => c !== '')) rows.push(row);
            row = [];
        } else field += ch;
    }
    if (field !== '' || row.length) {
        row.push(field);
        if (row.some((c) => c !== '')) rows.push(row);
    }
    return rows;
}

type UploadResult = {
    fileName: string;
    changed: number;
    unknown: number;
    info?: string;
    errors: string[];
};

// ── Audit history modal ───────────────────────────────────────────────────────
function HistoryModal({ item, onClose }: { item: string; onClose: () => void }) {
    const [rows, setRows] = useState<LeadTimeHistoryEntry[]>([]);

    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .getHistory(item)
            .then((h) => {
                if (live) setRows(h);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, [item]);

    const cell = (v: string) =>
        v ? <StatusIndicator type="success">{v}</StatusIndicator> : <>—</>;

    return (
        <Modal
            visible
            onDismiss={onClose}
            size="large"
            header={<Header variant="h2" description={item}>Audit history</Header>}
        >
            <Table
                variant="embedded"
                items={rows}
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No history for this item.
                    </Box>
                }
                columnDefinitions={[
                    { id: 'when', header: 'Date', cell: (h) => h.when },
                    { id: 'leadTime', header: 'Lead time', cell: (h) => cell(h.leadTime) },
                    { id: 'eos', header: 'End of Sale', cell: (h) => cell(h.eos) },
                    { id: 'eol', header: 'End of Life', cell: (h) => cell(h.eol) },
                    { id: 'hazard', header: 'Hazard class', cell: (h) => cell(h.hazard) },
                    { id: 'by', header: 'Changed by', cell: (h) => h.by },
                ]}
            />
        </Modal>
    );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function LeadTimeScreen() {
    const [rows, setRows] = useState<LeadTimeItem[]>([]);
    const [edits, setEdits] = useState<Record<string, Edit>>({});
    const [page, setPage] = useState(1);
    const [showChangedOnly, setShowChangedOnly] = useState(false);
    const [stage, setStage] = useState<'edit' | 'review' | 'saved'>('edit');
    const [historyItem, setHistoryItem] = useState<string | null>(null);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load the supplier item catalog from the service (empty until wired).
    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .listItems()
            .then((items) => {
                if (live) setRows(items);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, []);

    const isChanged = (r: LeadTimeItem) => {
        const e = edits[r.item];
        if (!e) return false;
        return (
            (e.leadTime !== undefined && e.leadTime !== null && e.leadTime !== r.current) ||
            (e.endOfSale !== undefined && e.endOfSale !== r.endOfSale) ||
            (e.endOfLife !== undefined && e.endOfLife !== r.endOfLife) ||
            (e.hazardClass !== undefined && e.hazardClass !== r.hazardClass) ||
            (e.dgGoods !== undefined && e.dgGoods !== r.dgGoods)
        );
    };

    const changedRows = useMemo(() => rows.filter(isChanged), [rows, edits]);
    const visibleRows = showChangedOnly ? changedRows : rows;
    const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageRows = visibleRows.slice(
        (safePage - 1) * PAGE_SIZE,
        (safePage - 1) * PAGE_SIZE + PAGE_SIZE,
    );

    const set = (item: string, patch: Edit) =>
        setEdits((prev) => ({ ...prev, [item]: { ...prev[item], ...patch } }));
    const leadOf = (r: LeadTimeItem) => {
        const v = edits[r.item]?.leadTime;
        return v === undefined || v === null ? '' : String(v);
    };
    const eosOf = (r: LeadTimeItem) => edits[r.item]?.endOfSale ?? r.endOfSale;
    const eolOf = (r: LeadTimeItem) => edits[r.item]?.endOfLife ?? r.endOfLife;
    const hazOf = (r: LeadTimeItem) => edits[r.item]?.hazardClass ?? r.hazardClass;
    const dgOf = (r: LeadTimeItem) => edits[r.item]?.dgGoods ?? r.dgGoods;

    const reset = () => {
        setEdits({});
        setPage(1);
        setShowChangedOnly(false);
        setStage('edit');
        setUploadResult(null);
    };

    const handleDownload = () => {
        const lines = [CSV_HEADERS.join(',')];
        rows.forEach((r) => {
            lines.push(
                [
                    r.item,
                    r.desc,
                    String(edits[r.item]?.leadTime ?? r.current),
                    eosOf(r),
                    eolOf(r),
                    hazOf(r),
                    dgOf(r) ? 'Yes' : 'No',
                    r.country,
                ]
                    .map((c) => csvEscape(String(c)))
                    .join(','),
            );
        });
        downloadCsv('lead-time-template.csv', lines.join('\n'));
    };

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const parsed = parseCsv(String(reader.result));
            if (parsed.length < 2) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ['File is empty or has no data rows.'],
                });
                return;
            }
            const header = parsed[0].map((h) => h.trim());
            const idx = (name: string) => header.indexOf(name);
            const iItem = idx('Item');
            const iLead = idx('LeadTimeDays');
            const iEos = idx('EndOfSale');
            const iEol = idx('EndOfLife');
            const iHaz = idx('HazardClass');
            if (iItem < 0) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ["Missing required 'Item' column."],
                });
                return;
            }

            const provided: string[] = [];
            if (iLead >= 0) provided.push('Lead time');
            if (iEos >= 0) provided.push('End of Sale');
            if (iEol >= 0) provided.push('End of Life');
            if (iHaz >= 0) provided.push('Hazard Class');

            const byId = new Map(rows.map((r) => [r.item, r]));
            const next: Record<string, Edit> = { ...edits };
            const errors: string[] = [];
            let changed = 0;
            let unknown = 0;

            for (let i = 1; i < parsed.length; i += 1) {
                const cells = parsed[i];
                const id = (cells[iItem] ?? '').trim();
                const r = id ? byId.get(id) : undefined;
                if (id && !r) {
                    unknown += 1;
                } else if (r) {
                    const patch: Edit = {};
                    if (iLead >= 0 && (cells[iLead] ?? '').trim() !== '') {
                        const n = Number(cells[iLead].trim());
                        if (Number.isNaN(n)) errors.push(`${id}: lead time is not a number`);
                        else if (n !== r.current) patch.leadTime = n;
                    }
                    if (iEos >= 0) {
                        const v = normalizeDate((cells[iEos] ?? '').trim());
                        if (v !== r.endOfSale) patch.endOfSale = v;
                    }
                    if (iEol >= 0) {
                        const v = normalizeDate((cells[iEol] ?? '').trim());
                        if (v !== r.endOfLife) patch.endOfLife = v;
                    }
                    if (iHaz >= 0) {
                        const v = (cells[iHaz] ?? '').trim();
                        if (v !== '' && v !== r.hazardClass) patch.hazardClass = v;
                    }
                    if (Object.keys(patch).length) {
                        next[id] = { ...next[id], ...patch };
                        changed += 1;
                    }
                }
            }
            const info = provided.length
                ? `Columns processed from file: ${provided.join(', ')}.`
                : undefined;
            setEdits(next);
            setUploadResult({ fileName: file.name, changed, unknown, info, errors });
            setShowChangedOnly(true);
            setPage(1);
        };
        reader.readAsText(file);
    };

    const saveAll = () => {
        const changes = changedRows.map((r) => ({ item: r.item, ...edits[r.item] }));
        getLeadTimeService()
            .saveChanges(changes)
            .finally(() => setStage('saved'));
    };

    const pageHeader = (
        <Header
            variant="h1"
            description="Update supplier item attributes. Country code is read-only. Edit across pages — changes are reviewed before saving."
        >
            Lead Time Update
        </Header>
    );

    // Saved confirmation.
    if (stage === 'saved') {
        return (
            <SpaceBetween size="l">
                {pageHeader}
                <Container>
                    <Box textAlign="center" padding="xl">
                        <SpaceBetween size="m">
                            <StatusIndicator type="success">
                                Supplier Item Attributes updated
                            </StatusIndicator>
                            <Box color="text-body-secondary">
                                {changedRows.length} item(s) submitted for review.
                            </Box>
                            <Button onClick={reset}>Done</Button>
                        </SpaceBetween>
                    </Box>
                </Container>
            </SpaceBetween>
        );
    }

    // Review before save.
    if (stage === 'review') {
        const diffCell = (isDiff: boolean, oldV: string, newV: string) =>
            isDiff ? (
                <Box>
                    <Box variant="span" color="text-status-inactive">
                        <s>{oldV || '—'}</s>
                    </Box>{' '}
                    →{' '}
                    <Box variant="span" fontWeight="bold" color="text-status-success">
                        {newV || '—'}
                    </Box>
                </Box>
            ) : (
                <>—</>
            );

        return (
            <SpaceBetween size="l">
                {pageHeader}
                <Table
                    variant="container"
                    items={changedRows}
                    header={
                        <Header
                            counter={`(${changedRows.length})`}
                            info={<Badge color="severity-medium">Confirm before saving</Badge>}
                        >
                            Review changes
                        </Header>
                    }
                    columnDefinitions={[
                        { id: 'item', header: 'Item', cell: (r) => r.item },
                        { id: 'country', header: 'Country', cell: (r) => r.country },
                        { id: 'desc', header: 'Description', cell: (r) => r.desc },
                        {
                            id: 'lead',
                            header: 'Lead time',
                            cell: (r) => {
                                const e = edits[r.item];
                                const changed =
                                    e.leadTime !== undefined &&
                                    e.leadTime !== null &&
                                    e.leadTime !== r.current;
                                return diffCell(changed, String(r.current), String(e.leadTime));
                            },
                        },
                        {
                            id: 'eos',
                            header: 'End of Sale',
                            cell: (r) => {
                                const e = edits[r.item];
                                const changed =
                                    e.endOfSale !== undefined && e.endOfSale !== r.endOfSale;
                                return diffCell(changed, r.endOfSale, e.endOfSale ?? '');
                            },
                        },
                        {
                            id: 'eol',
                            header: 'End of Life',
                            cell: (r) => {
                                const e = edits[r.item];
                                const changed =
                                    e.endOfLife !== undefined && e.endOfLife !== r.endOfLife;
                                return diffCell(changed, r.endOfLife, e.endOfLife ?? '');
                            },
                        },
                        {
                            id: 'hazard',
                            header: 'Hazard Class',
                            cell: (r) => {
                                const e = edits[r.item];
                                const changed =
                                    e.hazardClass !== undefined && e.hazardClass !== r.hazardClass;
                                return diffCell(changed, r.hazardClass, e.hazardClass ?? '');
                            },
                        },
                    ]}
                    footer={
                        <Box float="right">
                            <SpaceBetween size="xs" direction="horizontal">
                                <Button onClick={() => setStage('edit')}>Back to editing</Button>
                                <Button variant="primary" onClick={saveAll}>
                                    {`Save all ${changedRows.length} change(s)`}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                />
            </SpaceBetween>
        );
    }

    // Edit stage.
    return (
        <SpaceBetween size="l">
            {historyItem ? (
                <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
            ) : null}

            {pageHeader}

            {/* Bulk update via file */}
            <Container header={<Header variant="h2">Bulk update via file</Header>}>
                <ColumnLayout columns={2}>
                    <SpaceBetween size="s">
                        <Box fontWeight="bold">1. Download template</Box>
                        <Box color="text-body-secondary" fontSize="body-s">
                            A CSV of your items with current values. Opens in Excel — edit and
                            re-upload. Only the columns present in your file are updated.
                        </Box>
                        <Button iconName="download" onClick={handleDownload}>
                            Download template (CSV)
                        </Button>
                    </SpaceBetween>
                    <SpaceBetween size="s">
                        <Box fontWeight="bold">2. Upload edited file</Box>
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                    ev.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                            onDragOver={(ev) => ev.preventDefault()}
                            onDrop={(ev) => {
                                ev.preventDefault();
                                const f = ev.dataTransfer.files?.[0];
                                if (f) handleFile(f);
                            }}
                            style={{
                                cursor: 'pointer',
                                border: '2px dashed #b6bec9',
                                borderRadius: 12,
                                background: '#fbfbfb',
                                padding: '20px 16px',
                                textAlign: 'center',
                            }}
                        >
                            <SpaceBetween size="xxs">
                                <Box fontSize="heading-l">⬆</Box>
                                <Box>Drop CSV here or browse</Box>
                            </SpaceBetween>
                            <input
                                id="leadtime-file-input"
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                style={{ display: 'none' }}
                                onChange={(ev) => {
                                    const f = ev.target.files?.[0];
                                    if (f) handleFile(f);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                            />
                        </div>
                    </SpaceBetween>
                </ColumnLayout>
                {uploadResult ? (
                    <Box padding={{ top: 'm' }}>
                        <Alert
                            type={uploadResult.errors.length ? 'warning' : 'success'}
                            header={uploadResult.errors.length ? 'Imported with warnings' : 'Imported'}
                        >
                            <SpaceBetween size="xxs">
                                <Box fontSize="body-s" color="text-body-secondary">
                                    {uploadResult.fileName}
                                </Box>
                                <Box>
                                    {uploadResult.changed} record(s) changed and staged below
                                    {uploadResult.unknown > 0
                                        ? ` · ${uploadResult.unknown} unknown item(s) skipped`
                                        : ''}
                                </Box>
                                {uploadResult.info ? (
                                    <Box fontSize="body-s" color="text-body-secondary">
                                        {uploadResult.info}
                                    </Box>
                                ) : null}
                                {uploadResult.errors.slice(0, 6).map((e) => (
                                    <Box key={e} fontSize="body-s">
                                        {e}
                                    </Box>
                                ))}
                                {uploadResult.errors.length > 6 ? (
                                    <Box fontSize="body-s">
                                        …and {uploadResult.errors.length - 6} more
                                    </Box>
                                ) : null}
                            </SpaceBetween>
                        </Alert>
                    </Box>
                ) : null}
            </Container>

            {/* Editable item attributes table */}
            <Table
                variant="container"
                items={pageRows}
                header={
                    <Header
                        actions={
                            <Checkbox
                                checked={showChangedOnly}
                                onChange={({ detail }) => {
                                    setShowChangedOnly(detail.checked);
                                    setPage(1);
                                }}
                            >
                                Show changed only
                            </Checkbox>
                        }
                    >
                        Supplier Item Attributes
                    </Header>
                }
                pagination={
                    <Pagination
                        currentPageIndex={safePage}
                        pagesCount={pageCount}
                        onChange={({ detail }) => setPage(detail.currentPageIndex)}
                    />
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        {rows.length === 0 ? 'No items to show yet.' : 'No changed items yet.'}
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'item',
                        header: 'Item',
                        cell: (r) => (isChanged(r) ? `* ${r.item}` : r.item),
                    },
                    { id: 'desc', header: 'Description', cell: (r) => r.desc },
                    {
                        id: 'lead',
                        header: 'Lead time (days)',
                        cell: (r) => (
                            <Input
                                type="number"
                                value={leadOf(r)}
                                placeholder={String(r.current)}
                                onChange={({ detail }) =>
                                    set(r.item, {
                                        leadTime: detail.value === '' ? null : Number(detail.value),
                                    })
                                }
                            />
                        ),
                    },
                    {
                        id: 'eos',
                        header: 'End of Sale',
                        cell: (r) => (
                            <Input
                                value={eosOf(r)}
                                placeholder="YYYY-MM-DD"
                                onChange={({ detail }) => set(r.item, { endOfSale: detail.value })}
                            />
                        ),
                    },
                    {
                        id: 'eol',
                        header: 'End of Life',
                        cell: (r) => (
                            <Input
                                value={eolOf(r)}
                                placeholder="YYYY-MM-DD"
                                onChange={({ detail }) => set(r.item, { endOfLife: detail.value })}
                            />
                        ),
                    },
                    {
                        id: 'hazard',
                        header: 'Hazard Class',
                        cell: (r) => (
                            <Select
                                selectedOption={{ value: hazOf(r), label: hazOf(r) }}
                                options={HAZARD_OPTIONS}
                                onChange={({ detail }) =>
                                    set(r.item, { hazardClass: detail.selectedOption.value })
                                }
                            />
                        ),
                    },
                    {
                        id: 'dg',
                        header: 'DG Goods',
                        cell: (r) => (
                            <Button variant="inline-link" onClick={() => set(r.item, { dgGoods: !dgOf(r) })}>
                                <StatusIndicator type={dgOf(r) ? 'error' : 'success'}>
                                    {dgOf(r) ? 'Yes' : 'No'}
                                </StatusIndicator>
                            </Button>
                        ),
                    },
                    { id: 'country', header: 'Country', cell: (r) => r.country },
                    {
                        id: 'history',
                        header: 'History',
                        cell: (r) => (
                            <Button onClick={() => setHistoryItem(r.item)}>History</Button>
                        ),
                    },
                ]}
            />

            {/* Unsaved-changes bar (fixed layout wrapper; Cloudscape content inside) */}
            {changedRows.length > 0 ? (
                <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000 }}>
                    <Box
                        padding={{ vertical: 's', horizontal: 'l' }}
                        color="inherit"
                    // Dark bar via inline background; content uses Cloudscape components.
                    >
                        <div
                            style={{
                                background: '#000716',
                                borderRadius: 8,
                                padding: '12px 20px',
                            }}
                        >
                            <SpaceBetween
                                size="m"
                                direction="horizontal"
                                alignItems="center"
                            >
                                <span style={{ color: '#fff' }}>
                                    <strong>{changedRows.length}</strong> item(s) with unsaved
                                    changes across all pages
                                </span>
                                <span style={{ flex: 1 }} />
                                <Button variant="link" onClick={reset}>
                                    Discard all
                                </Button>
                                <Button
                                    onClick={() => {
                                        setShowChangedOnly(true);
                                        setPage(1);
                                    }}
                                >
                                    Show changed
                                </Button>
                                <Button variant="primary" onClick={() => setStage('review')}>
                                    Review &amp; save
                                </Button>
                            </SpaceBetween>
                        </div>
                    </Box>
                </div>
            ) : null}
        </SpaceBetween>
    );
}
